import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Warning,
  Error as ErrorIcon,
  Assignment,
  Build,
  CheckCircle,
  Engineering,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { maintenanceService } from '../../services/maintenanceService';
import EnhancedMaintenanceCompletionForm from './EnhancedMaintenanceCompletionForm';
import { StandardTaskAssignment } from '../../types/maintenance';

// TypeScript interfaces
interface TaskDataField {
  id: string;
  field_name: string;
  field_label: string;
  input_type: string;
  is_required: boolean;
  placeholder_text: string;
  help_text: string;
  options: string[];
  unit_of_measure: string;
  min_value: number | null;
  max_value: number | null;
  decimal_places: number;
  section_name: string;
  display_order: number;
  has_options: boolean;
  is_numeric: boolean;
}

interface AssignmentCompletionStatus {
  total_assignments: number;
  completed_assignments: number;
  overdue_assignments: number;
  due_today_assignments: number;
  upcoming_assignments: number;
  completion_percentage: number;
}

interface MaintenanceTaskData {
  id: string;
  task_name: string;
  task_description: string;
  site_name: string;
  site_code: string;
  next_due_date: string;
  days_until_due: number;
  is_overdue: boolean;
  frequency_display: string;
  priority: string;
  priority_display: string;
  status: string;
  status_display: string;
  
  // Assignment tracking
  total_assignments_count: number;
  due_assignments_count: number;
  has_overdue_assignments: boolean;
  overdue_assignments_count: number;
  is_partially_overdue: boolean;
  assignment_completion_status: AssignmentCompletionStatus;
  
  // Detailed assignment data
  task_assignments_detail: StandardTaskAssignment[];
  overdue_assignments_detail: StandardTaskAssignment[];
  
  // Resource information
  estimated_total_duration_hours: number;
  required_crew_size: number;
}

interface HierarchicalOverdueTaskManagerProps {
  siteName?: string;
  onTaskComplete?: (taskId: string) => void;
  onAssignmentComplete?: (taskId: string, assignmentId: string) => void;
}

// Work Package Card Component
interface WorkPackageCardProps {
  task: MaintenanceTaskData;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onTaskComplete?: (taskId: string) => void;
  onAssignmentComplete?: (taskId: string, assignmentId: string) => void;
}

const WorkPackageCard: React.FC<WorkPackageCardProps> = ({
  task,
  isExpanded,
  onToggleExpand,
  onTaskComplete,
  onAssignmentComplete,
}) => {
  const completionStatus = task.assignment_completion_status;
  
  // Determine status color
  const getStatusColor = () => {
    if (task.is_overdue) return 'error'; // Red - fully overdue
    if (task.is_partially_overdue) return 'warning'; // Orange - partially overdue
    return 'info'; // Blue - on schedule
  };

  const getStatusText = () => {
    if (task.is_overdue) return 'Fully Overdue';
    if (task.is_partially_overdue) return 'Partially Overdue';
    return 'On Schedule';
  };

  return (
    <Card sx={{ mb: 2, border: task.is_overdue ? '2px solid #f44336' : '1px solid #ddd' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {task.task_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip 
                label={task.site_name} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={getStatusText()} 
                size="small" 
                color={getStatusColor() as any}
              />
              <Chip 
                label={`${task.overdue_assignments_count} of ${task.total_assignments_count} overdue`} 
                size="small" 
                color="error"
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Due: {dayjs(task.next_due_date).format('MMM DD, YYYY')} 
              {task.days_until_due < 0 && (
                <Typography component="span" color="error.main" sx={{ ml: 1, fontWeight: 'bold' }}>
                  ({Math.abs(task.days_until_due)} days overdue)
                </Typography>
              )}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={onToggleExpand} size="small">
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Progress</Typography>
            <Typography variant="body2">
              {completionStatus.completed_assignments}/{completionStatus.total_assignments} completed
            </Typography>
          </Box>
          <Box sx={{ 
            width: '100%', 
            height: 8, 
            backgroundColor: '#f0f0f0', 
            borderRadius: 4 
          }}>
            <Box sx={{
              width: `${completionStatus.completion_percentage}%`,
              height: '100%',
              backgroundColor: '#4caf50',
              borderRadius: 4,
              transition: 'width 0.3s ease'
            }} />
          </Box>
        </Box>

        {/* Summary Stats */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: 1, 
          mb: 2,
          p: 1,
          backgroundColor: '#f9f9f9',
          borderRadius: 1
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="error.main">
              {completionStatus.overdue_assignments}
            </Typography>
            <Typography variant="caption">Overdue</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="warning.main">
              {completionStatus.due_today_assignments}
            </Typography>
            <Typography variant="caption">Due Today</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {completionStatus.completed_assignments}
            </Typography>
            <Typography variant="caption">Completed</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="info.main">
              {completionStatus.upcoming_assignments}
            </Typography>
            <Typography variant="caption">Upcoming</Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {task.overdue_assignments_count > 0 && (
            <Button 
              variant="contained" 
              color="error" 
              size="small"
              onClick={() => onTaskComplete?.(task.id)}
            >
              Complete Overdue Items
            </Button>
          )}
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => onTaskComplete?.(task.id)}
          >
            Complete All
          </Button>
        </Box>

        {/* Expanded Details */}
        <Collapse in={isExpanded}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Individual Task Assignments ({task.total_assignments_count})
          </Typography>
          
          {/* Overdue Assignments */}
          {task.overdue_assignments_detail.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                Overdue Assignments ({task.overdue_assignments_detail.length})
              </Typography>
              <List dense>
                {task.overdue_assignments_detail.map((assignment) => (
                  <ListItem key={assignment.id} sx={{ backgroundColor: '#ffebee', mb: 1, borderRadius: 1 }}>
                    <ListItemIcon>
                      <ErrorIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={assignment.task_name}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            Equipment: {assignment.equipment_name}
                          </Typography>
                          <Typography variant="caption" color="error.main">
                            Due: {dayjs(assignment.next_due_date).format('MMM DD, YYYY')}
                            {' '}(Frequency: {assignment.frequency_display})
                          </Typography>
                        </Box>
                      }
                    />
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="error"
                      onClick={() => onAssignmentComplete?.(task.id, assignment.id)}
                    >
                      Complete
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* All Assignments */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              All Assignments
            </Typography>
            <List dense>
              {task.task_assignments_detail.map((assignment) => {
                const isOverdue = new Date(assignment.next_due_date) < new Date();
                const isDueToday = dayjs(assignment.next_due_date).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
                
                return (
                  <ListItem key={assignment.id} sx={{ mb: 0.5, borderRadius: 1 }}>
                    <ListItemIcon>
                      {isOverdue ? (
                        <ErrorIcon color="error" />
                      ) : isDueToday ? (
                        <Warning color="warning" />
                      ) : (
                        <Assignment color="info" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={assignment.task_name}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            Equipment: {assignment.equipment_name}
                          </Typography>
                          <Typography variant="caption">
                            Due: {dayjs(assignment.next_due_date).format('MMM dd, yyyy')}
                            {' '}(Frequency: {assignment.frequency_display})
                          </Typography>
                        </Box>
                      }
                    />
                    <Chip 
                      label={isOverdue ? 'Overdue' : isDueToday ? 'Due Today' : 'Upcoming'} 
                      size="small" 
                      color={isOverdue ? 'error' : isDueToday ? 'warning' : 'default'}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Standalone Assignment Card Component
interface StandaloneAssignmentCardProps {
  assignment: StandardTaskAssignment;
  onAssignmentComplete?: (assignmentId: string) => void;
}

const StandaloneAssignmentCard: React.FC<StandaloneAssignmentCardProps> = ({
  assignment,
  onAssignmentComplete,
}) => {
  const isOverdue = new Date(assignment.next_due_date) < new Date();
  const daysOverdue = Math.floor((new Date().getTime() - new Date(assignment.next_due_date).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <Card sx={{ mb: 1, border: '1px solid #f44336' }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {assignment.task_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Equipment: {assignment.equipment_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip 
                label={`${daysOverdue} days overdue`} 
                size="small" 
                color="error" 
              />
              <Chip 
                label={assignment.frequency_display} 
                size="small" 
                variant="outlined" 
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="error.main">
              Due: {dayjs(assignment.next_due_date).format('MMM dd')}
            </Typography>
            <Button 
              size="small" 
              variant="contained" 
              color="error"
              onClick={() => onAssignmentComplete?.(assignment.id)}
            >
              Complete
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Main Component
const HierarchicalOverdueTaskManager: React.FC<HierarchicalOverdueTaskManagerProps> = ({
  siteName,
  onTaskComplete,
  onAssignmentComplete,
}) => {
  const [overdueWorkPackages, setOverdueWorkPackages] = useState<MaintenanceTaskData[]>([]);
  const [standaloneOverdueAssignments, setStandaloneOverdueAssignments] = useState<StandardTaskAssignment[]>([]);
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Enhanced completion form state
  const [completionFormOpen, setCompletionFormOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MaintenanceTaskData | null>(null);
  const [completionMode, setCompletionMode] = useState<'complete' | 'assign' | 'view'>('complete');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [draftData, setDraftData] = useState<any>(null);

  // Load overdue data function
  const loadOverdueData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch maintenance tasks with overdue assignments
      const tasksResponse = await fetch('/api/v1/maintenance/tasks/');
      const tasksData = await tasksResponse.json();
      
      // Filter tasks that have overdue assignments
      const overdueTasks = tasksData.results?.filter((task: MaintenanceTaskData) => 
        task.has_overdue_assignments || task.is_overdue
      ) || [];

      // Fetch standalone overdue assignments (not part of any maintenance task)
      const overdueAssignmentsResponse = await fetch('/api/v1/maintenance/task-assignments/overdue/');
      const overdueAssignmentsData = await overdueAssignmentsResponse.json();
      
      // Filter out assignments that are already part of maintenance tasks
      const taskAssignmentIds = new Set();
      overdueTasks.forEach((task: MaintenanceTaskData) => {
        task.task_assignments_detail.forEach(assignment => {
          taskAssignmentIds.add(assignment.id);
        });
      });
      
      const standalone = overdueAssignmentsData.filter((assignment: StandardTaskAssignment) => 
        !taskAssignmentIds.has(assignment.id)
      );

      setOverdueWorkPackages(overdueTasks);
      setStandaloneOverdueAssignments(standalone);
    } catch (err) {
      console.error('Failed to load overdue data:', err);
      setError('Failed to load overdue tasks data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when siteName changes
  useEffect(() => {
    loadOverdueData();
  }, [siteName]);

  const handleToggleExpand = (taskId: string) => {
    const newExpanded = new Set(expandedPackages);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedPackages(newExpanded);
  };

  const handleCompleteTask = async (taskId: string) => {
    // Find the task from the loaded data
    const task = overdueWorkPackages.find(t => t.id === taskId);
    if (!task) {
      console.error('Task not found:', taskId);
      return;
    }
    
    setSelectedTask(task);
    setCompletionMode('complete');
    
    // Load available technicians and any existing draft
    try {
      const [users, draft] = await Promise.all([
        maintenanceService.getAvailableTechnicians(task.id),
        maintenanceService.getCompletionDraft(task.id)
      ]);
      
      setAvailableUsers(users);
      // Set existing draft data if available
      setDraftData(draft.draft_data || null);
    } catch (error) {
      console.error('Error loading completion data:', error);
    }
    
    setCompletionFormOpen(true);
  };

  const handleAssignTask = async (task: MaintenanceTaskData) => {
    setSelectedTask(task);
    setCompletionMode('assign');
    
    // Load available technicians
    try {
      const users = await maintenanceService.getAvailableTechnicians(task.id);
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
    
    setCompletionFormOpen(true);
  };

  const handleCompletionSubmit = async (completionData: any) => {
    if (!selectedTask) return;
    
    try {
      if (completionMode === 'assign') {
        // Handle assignment
        await maintenanceService.assignTechnician(selectedTask.id, completionData.work_order);
        alert('Work order created and assigned successfully!');
      } else {
        // Handle completion
        await maintenanceService.completeTaskComprehensive(selectedTask.id, completionData);
        alert('Maintenance task completed successfully!');
        onTaskComplete(selectedTask.id);
      }
      
      setCompletionFormOpen(false);
      setSelectedTask(null);
      
      // Reload data
      loadOverdueData();
    } catch (error) {
      console.error('Error processing completion:', error);
      alert('Failed to process request. Please try again.');
    }
  };

  const handleSaveDraft = async (draftData: any) => {
    if (!selectedTask) return;
    
    try {
      await maintenanceService.saveCompletionDraft(selectedTask.id, draftData);
      
      // Update the current draft data so form stays populated
      setDraftData(draftData);
      
      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const totalOverdueCount = overdueWorkPackages.length + standaloneOverdueAssignments.length;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Overdue Tasks - Hierarchical View
      </Typography>

      {totalOverdueCount === 0 ? (
        <Alert severity="success" sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            All Caught Up!
          </Typography>
          <Typography>
            No overdue maintenance tasks or assignments at this time.
          </Typography>
        </Alert>
      ) : (
        <>
          {/* Summary */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1">
              <strong>{totalOverdueCount} overdue items</strong> require attention:
              {' '}{overdueWorkPackages.length} work packages and {standaloneOverdueAssignments.length} standalone tasks
            </Typography>
          </Alert>

          {/* Work Packages (MaintenanceTasks) */}
          {overdueWorkPackages.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: 'error.main' }}>
                Work Packages with Overdue Items ({overdueWorkPackages.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These are maintenance work packages containing multiple tasks. Click to expand and see individual assignments.
              </Typography>
              
              {overdueWorkPackages.map((task) => (
                <WorkPackageCard
                  key={task.id}
                  task={task}
                  isExpanded={expandedPackages.has(task.id)}
                  onToggleExpand={() => handleToggleExpand(task.id)}
                  onTaskComplete={handleCompleteTask}
                  onAssignmentComplete={onAssignmentComplete}
                />
              ))}
            </Box>
          )}

          {/* Standalone Overdue Assignments */}
          {standaloneOverdueAssignments.length > 0 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 2, color: 'error.main' }}>
                Standalone Overdue Tasks ({standaloneOverdueAssignments.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These are individual tasks that were missed or not completed and are not part of any work package.
              </Typography>
              
              {standaloneOverdueAssignments.map((assignment) => (
                <StandaloneAssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onAssignmentComplete={(assignmentId: string) => onAssignmentComplete?.(assignment.id, assignmentId)}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {/* Enhanced Completion Form */}
      {selectedTask && (
        <EnhancedMaintenanceCompletionForm
          open={completionFormOpen}
          onClose={() => {
            setCompletionFormOpen(false);
            setSelectedTask(null);
            setDraftData(null);
          }}
          onSubmit={handleCompletionSubmit}
          onSaveDraft={handleSaveDraft}
          task={selectedTask}
          mode={completionMode}
          existingData={draftData}
          availableUsers={availableUsers}
        />
      )}
    </Box>
  );
};

export default HierarchicalOverdueTaskManager; 